/* eslint-disable jsx-a11y/label-has-associated-control */
import cx from 'classnames';
import { h, Component } from 'preact';
import debounce from '../../utils/debounce';

// components and utils
import asyncComponent from './AsyncComponent';
import Typeahead from './Typeahead';
import { lookup } from '../../utils/legislative-districts';
import CountyViewer from './CountyViewer';

let sessionToken;
let autocompleteService;

const bounds = {
  east: -93.514764,
  west: -106.662177,
  south: 25.843902,
  north: 36.500199,
};

const componentRestrictions = { country: 'us' };

const types = ['geocode'];

const getPredictions = input => {
  return new Promise((resolve, reject) => {
    autocompleteService.getPlacePredictions(
      { bounds, componentRestrictions, input, sessionToken, types },
      (predict, status) => {
        if (status === 'OK') resolve(predict);

        reject(new Error('No results found.'));
      }
    );
  });
};

// always render what's provided, the geocoder decides
const shouldItemRender = () => true;

const AsyncCountyViewer = asyncComponent(() =>
  import(
    /* webpackPrefetch: true, webpackChunkName: "CountyViewer" */ './CountyViewer'
  )
);

class Lookup extends Component {
  constructor() {
    super();

    this.state = {
      coordinates: null,
      data: null,
      lookupError: false,
      lookupPending: false,
      results: [],
      value: '',
    };

    this.handleGeocode = this.handleGeocode.bind(this);
    this.debouncedHandleGeocode = debounce(this.handleGeocode, 300);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  componentDidMount() {
    // Initialize Google Maps services when component mounts
    console.log('componentDidMount called, google object:', typeof google);
    if (typeof google !== 'undefined') {
      console.log('Google Maps API found, initializing...');
      sessionToken = new google.maps.places.AutocompleteSessionToken();
      autocompleteService = new google.maps.places.AutocompleteService();
      this.placesService = new google.maps.places.PlacesService(this.inputRef);
      console.log('Google Maps services initialized successfully');
    } else {
      console.error('Google Maps API not found!');
    }
  }

  onClear() {
    this.setState({ value: '' });
  }

  onChange(value) {
    console.log('=== onChange called ===', 'value:', value);
    this.setState({ value }, () => {
      console.log('After setState, state is:', this.state);
      this.debouncedHandleGeocode();
    });
  }

  handleGeocode() {
    const { value: input } = this.state;
    console.log('=== handleGeocode called ===', 'input:', input);
    console.log('autocompleteService:', autocompleteService);
    console.log('sessionToken:', sessionToken);

    if (!autocompleteService) {
      console.error('autocompleteService not initialized');
      return;
    }

    if (!input || input.length === 0) {
      console.log('Input is empty, skipping prediction request');
      return;
    }

    console.log('=== Calling getPredictions ===');
    getPredictions(input)
      .then(results => {
        console.log('=== Got predictions ===:', results.length, 'items');
        this.setState({ results });
      })
      .catch((err) => {
        console.error('=== getPredictions error ===:', err);
        this.setState({ results: [] });
      });
  }

  onSelect({ name, placeId }) {
    this.setState({ lookupError: false, lookupPending: true, value: name });

    this.placesService.getDetails(
      {
        fields: ['geometry.location'],
        placeId,
        sessionToken,
      },
      result => {
        const {
          lat: latitude,
          lng: longitude,
        } = result.geometry.location.toJSON();

        const coordinates = { latitude, longitude };

        // looks up the county, sboe district, us house, tx house and tx senate districts
        lookup(coordinates)
          .then(({ data }) => {
            console.log('Lookup returned:', data);
            this.setState({ coordinates, data, lookupPending: false });
          })
          .catch((err) => {
            console.error('Lookup failed:', err);
            this.setState({
              data: null,
              lookupError: true,
              lookupPending: false,
            })
          });
      }
    );
  }

  render() {
    const {
      coordinates,
      data,
      lookupError,
      lookupPending,
      results,
      value,
    } = this.state;

    console.log('=== Lookup.render() ===', { 
      value, 
      resultsCount: results.length, 
      coordinates, 
      lookupPending 
    });

    let addressFeedback = null;

    if (lookupPending) {
      addressFeedback = (
        <p>
          Finding your details...{' '}
          <svg class="icon icon-spinner animate-pulse">
            <use xlinkHref="#icon-spinner" />
          </svg>
        </p>
      );
    }

    if (lookupError) {
      addressFeedback = (
        <p>
          Hmm... we were unable to find your county in Texas. Please check your
          address and try again!
        </p>
      );
    }

    if (coordinates && !lookupPending && !lookupError) {
      addressFeedback = <p>We found your county!</p>;
    }

    return (
      <div class="address-input">
        <label class="address-input__label t-sans" for="address-input">
          Enter an address, city or county:
        </label>
        <Typeahead
          inputProps={{
            id: 'address-input',
            class: 'address-input__input',
            placeholder: 'Harris County, TX, USA',
          }}
          items={results}
          getItemValue={item => ({
            name: item.description,
            placeId: item.place_id,
          })}
          value={value}
          onChange={this.onChange}
          onClear={this.onClear}
          onSelect={this.onSelect}
          renderItem={(item, isHighlighted) => (
            <li
              class={cx('listbox__item', {
                'listbox__item--highlighted': isHighlighted,
              })}
              key={item.id}
            >
              {item.description}
            </li>
          )}
          renderAttribution={() => (
            <li class="listbox__item listbox__item--attribution">
              <img
                alt="Powered by Google"
                src="../assets/images/powered-by-google.png"
              />
            </li>
          )}
          shouldItemRender={shouldItemRender}
          renderInput={props => (
            <input
              {...props}
              ref={el => {
                if (props.ref) props.ref(el);
                this.inputRef = el;
              }}
            />
          )}
        />
        <div class="address-feedback">{addressFeedback}</div>
        <div style={{ marginTop: '2rem' }}>
          <AsyncCountyViewer county={data ? data.county.name : 'Harris'} />
        </div>
        <p class="lookup-note">* Election day turnout data for the 2018 primaries is not available broken down by county. The Secretary of State only published turnout data for the 15 most populous counties in that election. Data for 2026 is preliminary and current as of 8 a.m. on March 5.</p>
         <p class="lookup-note">Credit: Apurva Mahajan</p>
      </div>
    );
  }
}

export default Lookup;
